import { OrganisationProductSummary } from "@fero/components";
import {
  useGetProductQuery,
  useGetPurchaserSavedProductPricesQuery,
} from "@fero/hooks";
import { usePurchaserSalePriceStore } from "@fero/stores";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { CurrencyInput } from "components/widgets/inputs/currency-input/currency-input";
import type React from "react";
import { AutoSizer, List, type ListRowProps } from "react-virtualized";
import { getVesselCapacity } from "utils/model-utils";
import { usePurchaserSalePriceHelpers } from "../hooks";

/**
 * Minimum fixed row height required by the virtualized table
 * If table had variable row heights it will require a more complex and performance heavy implementation
 * As we will need to pre-render cells in order to calculate their height
 */
const FIXED_ROW_HEIGHT = 100;

const BrowseProductsTableHeader = () => {
  return (
    <TableHead sx={{ display: "table", tableLayout: "fixed", width: "100%" }}>
      <TableRow>
        <TableCell sx={{ width: "40%" }}>Product</TableCell>
        <TableCell align="right" sx={{ width: "20%" }}>
          Sale Price
          <Typography fontWeight={400} fontSize={12}>
            inc. duty ex. VAT
          </Typography>
        </TableCell>
        <TableCell align="right" sx={{ width: "20%" }}>
          Sale Price
          <Typography fontWeight={400} fontSize={12}>
            inc. duty inc. VAT
          </Typography>
        </TableCell>
        <TableCell align="right" sx={{ width: "20%" }}>
          Sale Price
          <Typography fontWeight={400} fontSize={12}>
            ex. duty ex. VAT
          </Typography>
        </TableCell>
        <TableCell align="right" width={80} />
      </TableRow>
    </TableHead>
  );
};

interface TableCellWithLoadingProps {
  isLoading: boolean;
  isError: boolean;
  value?: string | number | null;
  align?: "left" | "center" | "right";
  columnWidth?: string | number;
}
const TableCellWithLoading: React.FC<TableCellWithLoadingProps> = ({
  isLoading,
  isError,
  value,
  align,
  children,
  columnWidth,
}) => {
  if (isLoading) {
    return (
      <TableCell align={align} sx={{ width: columnWidth }}>
        <Box sx={{ display: "flex", flex: 1, justifyContent: align }}>
          <Skeleton width={200} height={100} animation="pulse" />
        </Box>
      </TableCell>
    );
  }

  if (isError) {
    return <TableCell align={align}>--</TableCell>;
  }

  return (
    <TableCell sx={{ width: columnWidth }} align={align}>
      {children ? children : <p>{value}</p>}
    </TableCell>
  );
};

interface DiscountModalTableRowProps {
  productId: string;
  width: number;
  style: ListRowProps["style"];
  onRowDelete: (productId: string) => void;
}
const DiscountsModalTableRow: React.FC<DiscountModalTableRowProps> = ({
  productId,
  width,
  style,
  onRowDelete,
}) => {
  const { salePricesToSave, currency, organisationId, purchaserId } =
    usePurchaserSalePriceStore();
  const { updateSalePrice } = usePurchaserSalePriceHelpers();

  const {
    data: productData,
    isLoading: isLoadingProduct,
    isError: isErrorProduct,
  } = useGetProductQuery({ productId });
  const {
    data: allProductSalePrices,
    isLoading: isLoadingProductSalePrices,
    isError: isErrorGetSalePrice,
  } = useGetPurchaserSavedProductPricesQuery({ organisationId, purchaserId });

  const salePriceToSave =
    salePricesToSave.find((p) => p.productId === productId) ??
    allProductSalePrices?.body.find((p) => p.productId === productId) ??
    null;

  const onSalePriceChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    priceType: "incDutyExVatPrice" | "incDutyAndVatPrice" | "exDutyAndVatPrice",
  ) => {
    updateSalePrice(productId, { [priceType]: e.target.value });
  };

  return (
    <TableRow
      key={productId}
      sx={{
        display: "table",
        tableLayout: "fixed",
        width,
        height: FIXED_ROW_HEIGHT,
      }}
      style={style}
    >
      <TableCellWithLoading
        isLoading={isLoadingProduct}
        isError={isErrorProduct}
        columnWidth="40%"
      >
        {productData && (
          <OrganisationProductSummary
            code={productData?.value.code}
            name={productData?.value.name}
            producerName={productData?.value.producerName}
            productDate={productData?.value.productDate}
            unitType={productData?.value.unitType}
            vesselCapacity={getVesselCapacity(productData?.value.vesselSize)}
          />
        )}
      </TableCellWithLoading>
      <TableCellWithLoading
        isLoading={isLoadingProductSalePrices}
        isError={isErrorGetSalePrice}
        align="right"
        columnWidth="20%"
      >
        <CurrencyInput
          currency={currency}
          value={salePriceToSave?.incDutyExVatPrice ?? "0"}
          onChange={(e) => onSalePriceChange(e, "incDutyExVatPrice")}
        />
      </TableCellWithLoading>
      <TableCellWithLoading
        isLoading={isLoadingProductSalePrices}
        isError={isErrorGetSalePrice}
        align="right"
        columnWidth="20%"
      >
        <CurrencyInput
          currency={currency}
          value={salePriceToSave?.incDutyAndVatPrice ?? "0"}
          onChange={(e) => onSalePriceChange(e, "incDutyAndVatPrice")}
        />
      </TableCellWithLoading>
      <TableCellWithLoading
        isLoading={isLoadingProductSalePrices}
        isError={isErrorGetSalePrice}
        align="right"
        columnWidth="20%"
      >
        <CurrencyInput
          currency={currency}
          value={salePriceToSave?.exDutyAndVatPrice ?? "0"}
          onChange={(e) => onSalePriceChange(e, "exDutyAndVatPrice")}
        />
      </TableCellWithLoading>
      <TableCell align="right" width={80}>
        <IconButton onClick={() => onRowDelete(productId)} aria-label="delete">
          <DeleteOutlineIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

interface PurchaserDiscountsModalTableProps {
  discountedProductIds: string[];
  onRemoveDiscount: (productId: string) => void;
}
export const PurchaserDiscountsModalTable: React.FC<
  PurchaserDiscountsModalTableProps
> = ({ discountedProductIds, onRemoveDiscount }) => {
  if (discountedProductIds.length === 0) {
    return (
      <TableContainer component={Paper} elevation={0}>
        <Table>
          <BrowseProductsTableHeader />
          <TableBody>
            <TableRow sx={{ justifyContent: "flex-start" }}>
              <TableCell colSpan={4} align="center">
                No discounts found, search for a product to add a discount
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        flex: 1,
        display: "flex",
        minHeight: 300,
      }}
    >
      <AutoSizer>
        {({ width, height }) => (
          <Table>
            <BrowseProductsTableHeader />
            <TableBody sx={{ flex: 1, display: "flex", width: "100%" }}>
              <List
                autoContainerWidth
                height={height - 100}
                rowCount={discountedProductIds.length}
                width={width}
                rowHeight={FIXED_ROW_HEIGHT}
                rowRenderer={({ index, style }) => {
                  const productId = discountedProductIds[index];
                  return (
                    <DiscountsModalTableRow
                      key={productId}
                      productId={productId}
                      width={width}
                      style={style}
                      onRowDelete={onRemoveDiscount}
                    />
                  );
                }}
              />
            </TableBody>
          </Table>
        )}
      </AutoSizer>
    </TableContainer>
  );
};
